
import VueApp from '@/@types/app/VueApp';
import { WorkspaceViewArticleItem } from '@/@types/WorkspaceViewArticleItem';
import { Component, Watch } from 'vue-property-decorator';
import SidebarArticleItem from '../SidebarArticleItem/SidebarArticleItem.vue';
import SidebarGroupItem from '../SidebarGroupItem/SidebarGroupItem.vue';
import WorkspaceToolbar from './WorkspaceToolbar.vue';
import { Article } from '@/models/article/Article';
import EventBus from '@/EventBus';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import articleEditService from '@/services/article/ArticleEditService';
import ArticlesReorder from '../ArticlesReorder.vue';
import { commitSetArticleIdToExpand } from '@/store/commits/sharedCommits';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import '@/scss/sidebar-menu.scss';
import './WorkspaceSidebar.scss';
import { dispatchGetArticleCommentsCount } from '@/store/dispatchers/articleCommentsDispatchers';
import { CHECK_PARAGRAPH_PROGRESS, PARAGRAPH_PROGRESS_UPDATE } from '@/events';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import { Workspace } from '@/models/Workspace';
import { WorkspaceView } from '@/@types/WorkspaceView';
import { WorkspaceOrder } from '@/@types/WorkspaceOrder';
import slugify from 'slugify';
import { dispatchLoadConnectors, dispatchDisconnectConnector } from '@/store/dispatchers/connectorDispatchers';
import connectorSyncMonitor from '@/services/ConnectorSyncMonitor';
import { dispatchOpenSelectedArticle } from '@/store/dispatchers/workspaceViewDispatchers';
import { commitSetSelectedArticle } from '@/store/commits/workspaceViewCommits';
import { dispatchGetPersonalArticlesCount } from '@/store/dispatchers/uiDispatchers';
import navigationService from '@/services/NavigationService';
import urlService from '@/services/UrlService';
import { ConnectorConfig } from '@/models/ConnectorConfig';
import { Loading } from 'element-ui';

const ConnectorWizard = () => import('@/components/connectors/ConnectorWizard.vue');
const ConnectorEditDialog = () => import('@/components/connectors/ConnectorEditDialog.vue');
const DisconnectConfirmDialog = () => import('@/components/connectors/DisconnectConfirmDialog.vue');
const ReconnectDialog = () => import('@/components/connectors/ReconnectDialog.vue');

@Component({
  components: {
    SidebarArticleItem,
    SidebarGroupItem,
    ArticlesReorder,
    WorkspaceToolbar,
    ConnectorWizard,
    ConnectorEditDialog,
    DisconnectConfirmDialog,
    ReconnectDialog,
  },
})
export default class WorkspaceSidebar extends VueApp {
  article: Article;
  hasArticles = -1;
  toggle: false;
  paragraphProgress = 0;
  nestedArticlesData: {
    workspace: Workspace;
    workspaceView: WorkspaceView;
    showArchivedArticles: boolean;
    articleMode: ArticleEditMode;
    isMobileView: boolean;
  };

  expanded: string[] = [];
  showConnectorWizard = false;
  editingConnector: ConnectorConfig | null = null;
  removingConnector: ConnectorConfig | null = null;
  reconnectingSourceType: string | null = null;
  isDisconnecting = false;

  get currentWorkspaceId(): string {
    return this.workspace?.id || '';
  }

  get workspace(): Workspace {
    return this.$store.state.workspaceView.workspace;
  }

  get workspaceView(): WorkspaceView {
    return workspaceService.workspaceView;
  }

  get workspaceOrder(): WorkspaceOrder[] {
    if (this.workspaceView.workspace) {
      return this.workspaceView.workspace.order;
    }
    return null;
  }

  get showArchivedArticles(): boolean {
    return this.workspaceView.showArchivedArticles;
  }

  get articleMode(): ArticleEditMode {
    return this.$store.state.articleMode;
  }

  get isMobileView(): boolean {
    return mobileResponsiveService.isMobileView;
  }

  get isReorderMode(): boolean {
    return this.$store.state.isReorderMode;
  }

  get articleEditor() {
    return this.$store.state.articleEditor;
  }

  get isWorkspaceArchived(): boolean {
    const workspace = this.$store.state.workspaceView.workspace ?? null;
    if (workspace) {
      return !!workspace.isArchived;
    }
    return null;
  }

  updateExpandedState(article?: Article) {
    const rootArticle = article || this.workspaceView.selectedArticle;
    const workspaceOrder = this.workspaceView.workspace?.order;

    if (this.workspaceView.isSharedMode) {
      this.expanded = [this.workspaceView.selectedArticle.id];
      return '';
    }

    if (workspaceOrder && rootArticle) {
      // This service finds all parent IDs needed to reveal the rootArticle
      const idsToExpand = articleEditService.findArticlesToExpand(workspaceOrder, rootArticle.id);

      // Also expand the selected article itself if it has paragraphs or child articles
      const hasNestedContent =
        rootArticle.paragraphs?.length > 0 ||
        (this.findArticleInOrder({ value: rootArticle.id, items: [] }) &&
          this.workspaceOrder.find((o) => o.value === rootArticle.id)?.items?.length > 0);

      if (hasNestedContent && !idsToExpand.includes(rootArticle.id)) {
        idsToExpand.push(rootArticle.id);
      }

      // Also expand groups in the path to the article
      const groupsInPath = this.findGroupsInPath(workspaceOrder, rootArticle.id);
      groupsInPath.forEach((groupId) => {
        if (!idsToExpand.includes(groupId)) {
          idsToExpand.push(groupId);
        }
      });

      // Find and expand parent articles that contain the target
      const parentArticles = this.findParentArticles(workspaceOrder, rootArticle.id);
      parentArticles.forEach((articleId) => {
        if (!idsToExpand.includes(articleId)) {
          idsToExpand.push(articleId);
        }
      });

      this.expanded = idsToExpand;
    }
  }

  /**
   * Find all parent articles that contain the target article/group in their nested items
   * @param order - The workspace order tree
   * @param targetId - The target article/group ID
   * @returns Array of parent article IDs
   */
  findParentArticles(order: WorkspaceOrder[], targetId: string): string[] {
    const parentArticles: string[] = [];

    const traverse = (items: WorkspaceOrder[]): void => {
      for (const item of items) {
        if (item.type === 'group') {
          // Recursively check group items
          if (item.items && item.items.length > 0) {
            traverse(item.items);
          }
        } else if (item.items && item.items.length > 0) {
          // This is an article with nested items
          if (this.findInTree(item.items, targetId)) {
            parentArticles.push(item.value);
          }
          // Continue traversing nested items
          traverse(item.items);
        }
      }
    };

    traverse(order);
    return parentArticles;
  }

  /**
   * Find all parent group IDs for a given target (article or group)
   * Used for expanding the correct groups when navigating to an article via URL
   *
   * @param order - The workspace order tree to search
   * @param targetId - The article or group ID to find parents for
   * @returns Array of group IDs from root to the target's immediate parent
   *
   * @example
   * // Given tree: group1 -> group2 -> article123
   * findGroupsInPath(order, 'article123') // Returns: ['group1', 'group2']
   */
  findGroupsInPath(order: WorkspaceOrder[], targetId: string): string[] {
    const groups: string[] = [];

    const traverse = (items: WorkspaceOrder[], parents: string[]): boolean => {
      for (const item of items) {
        if (item.type === 'group') {
          const newParents = [...parents, item.value];
          if (this.findInTree(item.items, targetId)) {
            groups.push(...newParents);
            return true;
          }
          if (traverse(item.items, newParents)) {
            return true;
          }
        } else if (item.value === targetId) {
          groups.push(...parents);
          return true;
        } else if (item.items && item.items.length > 0) {
          // Article with nested items - check if target is within
          if (this.findInTree(item.items, targetId)) {
            // Found target in article's nested items, include all current parents
            groups.push(...parents);
            // Now traverse the article's items to find nested groups
            const nestedGroups: string[] = [];
            this.traverseForNestedGroups(item.items, targetId, nestedGroups);
            groups.push(...nestedGroups);
            return true;
          }
        }
      }
      return false;
    };

    traverse(order, []);
    return groups;
  }

  /**
   * Helper method to find nested groups within an article's items
   * @param items - The nested items to search
   * @param targetId - The target article/group ID
   * @param foundGroups - Array to accumulate found group IDs
   */
  traverseForNestedGroups(items: WorkspaceOrder[], targetId: string, foundGroups: string[]): boolean {
    for (const item of items) {
      if (item.type === 'group') {
        if (this.findInTree(item.items, targetId) || item.value === targetId) {
          foundGroups.push(item.value);
          if (item.value === targetId) {
            return true;
          }
          if (this.traverseForNestedGroups(item.items, targetId, foundGroups)) {
            return true;
          }
        }
      } else if (item.value === targetId) {
        return true;
      } else if (item.items && item.items.length > 0) {
        if (this.traverseForNestedGroups(item.items, targetId, foundGroups)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Check if a target ID exists anywhere in the order tree (recursive)
   *
   * @param order - The workspace order tree to search
   * @param targetId - The article or group ID to find
   * @returns true if found, false otherwise
   */
  findInTree(order: WorkspaceOrder[], targetId: string): boolean {
    for (const item of order) {
      if (item.value === targetId) {
        return true;
      }
      if (item.items && item.items.length > 0) {
        if (this.findInTree(item.items, targetId)) {
          return true;
        }
      }
    }
    return false;
  }

  toggleExpand(item: Article | WorkspaceViewArticleItem | WorkspaceOrder) {
    const itemId = 'id' in item ? item.id : item.value;
    const index = this.expanded.indexOf(itemId);
    if (index > -1) {
      this.expanded.splice(index, 1);
    } else {
      this.expanded.push(itemId);
    }
  }

  /**
   * Find the first non-archived article within a group (recursive search)
   * Searches through nested groups to find the first accessible article
   *
   * @param groupNode - The group node to search within
   * @returns The first article found, or null if no articles or all archived
   *
   * @example
   * const firstArticle = findFirstArticleInGroup(groupNode);
   * if (firstArticle) {
   *   // Navigate to firstArticle
   * }
   */
  findFirstArticleInGroup(groupNode: WorkspaceOrder): WorkspaceViewArticleItem | Article | null {
    if (!groupNode.items || groupNode.items.length === 0) {
      return null;
    }

    for (const item of groupNode.items) {
      if (item.type === 'group') {
        // Recursively search in nested group
        const found = this.findFirstArticleInGroup(item);
        if (found) return found;
      } else {
        // Found an article
        const article = this.findArticleInOrder(item);
        if (article && (!article.isArchived || this.showArchivedArticles)) {
          return article;
        }
      }
    }

    return null;
  }

  handleNavigateToFirstArticle(payload: { groupNode: WorkspaceOrder; newTab?: boolean }) {
    const { groupNode, newTab } = payload;

    // Expand the group (don't toggle, just ensure it's expanded)
    const groupId = groupNode.value;
    if (!this.expanded.includes(groupId)) {
      this.expanded.push(groupId);
    }

    // Find first article in group
    const firstArticle = this.findFirstArticleInGroup(groupNode);

    if (firstArticle) {
      // Navigate to the article
      const workspace = this.workspaceView.workspace;

      if (workspace && workspace.slug) {
        // Generate article slug from title since WorkspaceViewArticleItem doesn't have slug property
        const articleSlug = slugify(firstArticle.title);
        const url = `/b/${workspace.shortId}/${workspace.slug}/${firstArticle.shortId}/${articleSlug}`;

        if (newTab) {
          // For new tab, use full URL with hash
          const fullUrl = `${window.location.origin}${window.location.pathname}#${url}`;
          window.open(fullUrl, '_blank');
        } else {
          // Use router.replace to avoid duplicating hash
          this.$router.replace(url);
        }
      }
    }
  }

  get canManageIntegrations(): boolean {
    const workspace = this.workspace;
    if (!workspace) return false;
    return workspace.canEditArticles();
  }

  openConnectorWizard() {
    this.showConnectorWizard = true;
  }

  closeConnectorWizard() {
    this.showConnectorWizard = false;
  }

  handleEditIntegration(connector: ConnectorConfig) {
    this.editingConnector = connector;
  }

  handleRemoveIntegration(connector: ConnectorConfig) {
    this.removingConnector = connector;
  }

  handleReconnectIntegration(sourceType: string) {
    this.reconnectingSourceType = sourceType;
  }

  async handleReconnectSuccess(result: Record<string, unknown>) {
    this.reconnectingSourceType = null;
    if (result?.workspaceOrder) {
      this.$store.commit('setWorkspaceOrder', result.workspaceOrder);
    }
    await workspaceService.loadArticlesItems();
    const selectedId = this.$store.state.workspaceView?.selectedArticle?.id;
    if (selectedId) {
      await dispatchOpenSelectedArticle({ articleId: selectedId });
    }
  }

  async handleConfirmRemove(keepArticlesOrConnectorId?: boolean | string) {
    // When called from DisconnectConfirmDialog, first arg is keepArticles (boolean).
    // When called from edit dialog, first arg may be a connectorId (string).
    const isKeepArticles = typeof keepArticlesOrConnectorId === 'boolean';
    const keepArticles = isKeepArticles ? keepArticlesOrConnectorId : false;
    const connectorIdFromEdit = isKeepArticles ? undefined : keepArticlesOrConnectorId;
    const connectorId = connectorIdFromEdit ?? this.removingConnector?.id ?? this.editingConnector?.id ?? null;
    if (connectorId) {
      const loader = Loading.service({ lock: true });
      this.isDisconnecting = true;
      try {
        const result = await dispatchDisconnectConnector(connectorId, keepArticles);
        if (result?.workspaceOrder) {
          this.$store.commit('setWorkspaceOrder', result.workspaceOrder);
        }
        await workspaceService.loadArticlesItems();
        await dispatchGetPersonalArticlesCount();
        if (keepArticles) {
          const selectedId = this.$store.state.workspaceView?.selectedArticle?.id;
          if (selectedId) {
            await dispatchOpenSelectedArticle({ articleId: selectedId });
          }
        } else {
          commitSetSelectedArticle(null);
          await navigationService.navigate(urlService.getWorkspaceViewRoute(this.workspace));
        }
      } finally {
        loader.close();
        this.isDisconnecting = false;
        this.removingConnector = null;
        this.editingConnector = null;
      }
    }
  }

  handleCancelRemove() {
    this.removingConnector = null;
  }

  handleEditConfirm() {
    this.editingConnector = null;
  }

  handleEditCancel() {
    this.editingConnector = null;
  }

  handleSave() {
    EventBus.$emit('save-reorder-articles');
  }

  handleCancel() {
    EventBus.$emit('close-reorder-articles');
  }

  findArticleInOrder(order: WorkspaceOrder) {
    return this.$store.state.workspaceView.articles.find((article) => article.id === order.value) || null;
  }

  getTotalHeight() {
    requestAnimationFrame(() => {
      const list = this.$refs.articlesList as any;

      if (list) {
        const litsHeight = list.$el.scrollHeight;
        const minDraggableAreaHeight = litsHeight + 83; // height of: workspaceName 64px, lable 'article' 19px
        EventBus.$emit('min-draggable-area-height-changed', minDraggableAreaHeight);
      }
    });
  }

  setNestedArticlesData() {
    this.nestedArticlesData = {
      workspace: this.workspace,
      workspaceView: this.workspaceView,
      showArchivedArticles: this.showArchivedArticles,
      articleMode: this.articleMode,
      isMobileView: this.isMobileView,
    };
  }

  handleParagraphProgressChange(progress = 0) {
    this.paragraphProgress = progress;
  }

  created() {
    this.setNestedArticlesData();
    EventBus.$on(PARAGRAPH_PROGRESS_UPDATE, this.handleParagraphProgressChange);
    EventBus.$emit(CHECK_PARAGRAPH_PROGRESS);
    if (this.currentWorkspaceId) {
      this.loadConnectorsAndInitMonitor(this.currentWorkspaceId);
    }
  }

  async loadConnectorsAndInitMonitor(workspaceId: string) {
    await dispatchLoadConnectors(workspaceId);
    const connectors = (this.$store.state.connector?.connectors ?? []) as ConnectorConfig[];
    const ids = connectors.map((c) => c.id).filter((id): id is string => Boolean(id));
    await connectorSyncMonitor.init(ids);
  }

  mounted() {
    this.updateExpandedState();
  }

  updated() {
    this.setNestedArticlesData();
  }

  beforeDestroy() {
    EventBus.$off(PARAGRAPH_PROGRESS_UPDATE, this.handleParagraphProgressChange);
    connectorSyncMonitor.dispose();
  }

  @Watch('workspaceView.articles', { deep: false })
  onArticlesChanged(articles: Article[]) {
    this.hasArticles = articles.length;
    this.updateExpandedState();
    // timeout to ensure all already rendered
    this.getTotalHeight();
  }

  @Watch('$route', { immediate: true, deep: true })
  onUrlChange() {
    this.getTotalHeight();
    EventBus.$emit('scroll-to-article');
  }

  @Watch('currentWorkspaceId')
  onWorkspaceIdChanged(newId: string) {
    if (newId) {
      this.loadConnectorsAndInitMonitor(newId);
    }
  }

  @Watch('workspaceView.selectedArticle')
  onSelectedArticleChanged(article: Article) {
    this.updateExpandedState(article);

    const articleIdToExpand = this.$store.state.articleIdToExpand;
    if (article) {
      setTimeout(() => {
        EventBus.$emit('scroll-to-article');
      }, 10);
      if (articleIdToExpand === article.shortId || articleIdToExpand === article.id) {
        commitSetArticleIdToExpand(null);
      }
      dispatchGetArticleCommentsCount();
    }
  }
}
